import { css } from '@emotion/react'
import { useAlert } from 'components/alert'
import { useSession } from 'components/auth/useSession'
import { isLotacaoOrEstagio } from 'components/auth/useSessionUtils'
import { useErrorHandler } from 'components/error'
import { Form, FormRenderProps } from 'components/form'
import { LoadingIndicator } from 'components/loading'
import { information } from 'components/modals/information'
import { HideOnScreenSize } from 'components/responsive'
import { TermoModal } from 'components/TermoModal'
import { useIsParticipantePermitidoVideochamadaQuery, useVideochamadaViewQuery } from 'graphql/hooks.generated'
import { StatusVideochamadaEnum } from 'graphql/types.generated'
import { useUrlQueryParams } from 'hooks/useUrlQueryParams'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { Route, useHistory, useLocation, useRouteMatch } from 'react-router'
import { isUndefinedOrNull } from 'util/checks'
import { capitalize } from 'util/lang'

import { VideochamadaToastProvider } from './componentes/toast/useVideochamadaToast'
import { CriarVideochamadaView } from './CriarVideochamadaView'
import { useSolicitarEntradaVideochamada } from './hooks/useSolicitarEntradaVideochamada'
import { useVideochamadaStatus } from './hooks/useVideochamadaStatus'
import { useStoredAudioEnabled, useStoredVideoEnabled } from './hooks/useVideochamadaStorage'
import {
  AtorVideochamada,
  ENTRAR_DIRETO_QUERY_PARAM,
  LocalVideocallParticipant,
  MotivoEncerramentoVideochamadaEnum,
  SalaEsperaFormModel,
  StatusSolicitacaoEntradaVideochamadaEnum,
  VideochamadaQueryParams,
  VideochamadaReconexaoLocationState,
  VideochamadaUrlParams,
} from './model-videochamada'
import { SalaEsperaVideochamadaAnfitriaoView } from './SalaEsperaVideochamadaAnfitriaoView'
import { SalaEsperaVideochamadaUsuariosExternosView } from './SalaEsperaVideochamadaUsuariosExternosView'
import { SalaEsperaVideochamadaUsuariosExternosViewMobile } from './SalaEsperaVideochamadaUsuariosExternosViewMobile'
import { termosVideochamadaProfissionalPec, termosVideochamadaProfissionalVisitante } from './termos'
import { isAnfitriaoForaDaChamada, showAtmosphereErrorModal, showConnectionFailModal } from './utils-videochamada'
import { profParticipanteValidator } from './validator-videochamada'
import { VideochamadaCanceladaView } from './VideochamadaCanceladaView'
import { VideochamadaEncerradaView } from './VideochamadaEncerradaView'
import { VideochamadaView } from './VideochamadaView'

export function VideochamadaRootView() {
  const { data: session, loading: isSessionLoading, refresh: refreshSession } = useSession()
  const history = useHistory()
  const alert = useAlert()
  const handleRejection = useErrorHandler()
  const [audioEnabled, setAudioEnabled] = useStoredAudioEnabled(true)
  const [videoEnabled, setVideoEnabled] = useStoredVideoEnabled(true)

  const {
    params: { uuid: videochamadaUuid },
    url: matchUrl,
  } = useRouteMatch<VideochamadaUrlParams>()

  const { entrarDireto } = useUrlQueryParams<VideochamadaQueryParams>()
  const isEntrarDireto = entrarDireto === ENTRAR_DIRETO_QUERY_PARAM.entrarDireto

  const { loading, data: dataVideochamada } = useVideochamadaViewQuery({ variables: { uuid: videochamadaUuid } })

  const idAcesso = session?.acesso?.id
  const owner = dataVideochamada?.videochamada?.owner

  const isOwner = !isUndefinedOrNull(idAcesso) && idAcesso === owner?.id
  const ownerName = owner?.profissional.nome
  const ownerCpf = owner?.profissional.cpf
  const cboProfissional =
    dataVideochamada?.videochamada && owner && isLotacaoOrEstagio(owner) ? capitalize(owner.cbo.nome) : null

  const [selfData, setSelfData] = useState<LocalVideocallParticipant>()
  const [reingressoTermoReadonly, setReingressoTermoReadonly] = useState(false)

  const { state: reconnectState } = useLocation<VideochamadaReconexaoLocationState>()

  const { data: participantePermitidoData, loading: isCheckingReconnect } = useIsParticipantePermitidoVideochamadaQuery(
    {
      variables: { input: { videochamadaUuid, participanteId: reconnectState?.selfData?.id ?? '' } },
      skip: !reconnectState?.reconnecting || !reconnectState?.selfData?.id,
    }
  )

  const openTermosResponsabilidadeModal = useCallback(() => {
    history.push(`${matchUrl}/termosResponsabilidade`)
  }, [history, matchUrl])

  const closeTermosResponsabilidadeModal = useCallback(() => {
    history.replace(history.location.pathname.replace('/termosResponsabilidade', ''))
  }, [history])

  const { solicitarEntradaVideochamada, statusSolicitacao } = useSolicitarEntradaVideochamada({
    videochamadaUuid,
    ownerCpf,
    onEntrar: setSelfData,
    isOwner,
  })

  const {
    loading: isStatusVideochamadaLoading,
    status,
    motivoEncerramento,
    hasConnectionFailed,
  } = useVideochamadaStatus(videochamadaUuid, true)

  const isAnfitriaoForaDaChamadaAtual = isAnfitriaoForaDaChamada({ isOwner, isParticipando: !!selfData, status })

  useEffect(() => {
    switch (statusSolicitacao) {
      case StatusSolicitacaoEntradaVideochamadaEnum.NEGADA:
        alert('danger', `Sua participação na videochamada foi negada por ${ownerName}.`)
        break
      case StatusSolicitacaoEntradaVideochamadaEnum.TIMEOUT:
        alert('danger', `Sua solicitação para entrar na chamada não foi respondida por ${ownerName}, tente novamente.`)
        break
      case StatusSolicitacaoEntradaVideochamadaEnum.VIDEOCHAMADA_LOTADA:
        information({
          title: 'Número máximo de participantes excedido',
          body: 'No momento, as videochamadas e-SUS APS podem ter apenas dois participantes simultâneos.',
          showCloseButton: true,
        })()
        break
    }
  }, [alert, ownerName, statusSolicitacao])

  useEffect(() => {
    if (status === StatusVideochamadaEnum.NAO_ENCONTRADA) history.replace('/404')
    if (status === StatusVideochamadaEnum.ENCERRADA) navegarPorEncerramento(motivoEncerramento, isOwner, history)
  }, [history, isOwner, motivoEncerramento, status])

  // O param só vale para a entrada recém-criada: consumi-lo faz o reingresso seguinte (e uma falha aqui) cair na sala de espera
  useEffect(() => {
    if (!isSessionLoading && isAnfitriaoForaDaChamadaAtual && isEntrarDireto)
      solicitarEntradaVideochamada(ownerName, ownerCpf, true)
        .catch(handleRejection)
        // Só consome o param se a tela ainda for a da chamada: um encerramento durante a entrada já navegou para outra rota
        .finally(() => history.location.pathname === matchUrl && history.replace(matchUrl))
  }, [
    solicitarEntradaVideochamada,
    isAnfitriaoForaDaChamadaAtual,
    ownerCpf,
    ownerName,
    handleRejection,
    isSessionLoading,
    isEntrarDireto,
    history,
    matchUrl,
  ])

  const hasShownAtmosphereModal = useRef(false)

  useEffect(() => {
    if (hasConnectionFailed && !selfData && !hasShownAtmosphereModal.current) {
      hasShownAtmosphereModal.current = true
      showAtmosphereErrorModal(isOwner, () => history.push('/videochamada/encerrada'))
    }
  }, [hasConnectionFailed, selfData, isOwner, history])

  const isAwaitingReconnect =
    !!reconnectState?.reconnecting &&
    !selfData &&
    (isCheckingReconnect || !!participantePermitidoData?.isParticipantePermitidoVideochamada)

  useEffect(() => {
    if (
      reconnectState?.reconnecting &&
      reconnectState?.selfData &&
      !selfData &&
      participantePermitidoData?.isParticipantePermitidoVideochamada
    ) {
      setSelfData(reconnectState.selfData)
    }
  }, [reconnectState, participantePermitidoData, selfData])

  const handleEntrarButtonClick = () => {
    refreshSession()
    openTermosResponsabilidadeModal()
  }

  const handleAnfitriaoEntrarButtonClick = useCallback(() => {
    setReingressoTermoReadonly(false)
    refreshSession()
    openTermosResponsabilidadeModal()
  }, [refreshSession, openTermosResponsabilidadeModal])

  const handleVisualizarTermoAnfitriao = useCallback(() => {
    setReingressoTermoReadonly(true)
    openTermosResponsabilidadeModal()
  }, [openTermosResponsabilidadeModal])

  const handleAnfitriaoAceitarTermo = useCallback(
    (autorizacoes: Record<string, boolean>) => {
      closeTermosResponsabilidadeModal()
      solicitarEntradaVideochamada(ownerName, ownerCpf, autorizacoes[AtorVideochamada.PROFISSIONAL]).catch(
        handleRejection
      )
    },
    [closeTermosResponsabilidadeModal, solicitarEntradaVideochamada, ownerName, ownerCpf, handleRejection]
  )

  const handleSolicitarEntrada = useCallback(
    async (values: SalaEsperaFormModel, autorizacoes: Record<string, boolean>) => {
      if (!autorizacoes[AtorVideochamada.PROFISSIONAL_EXTERNO])
        alert('danger', 'O profissional precisa autorizar a videochamada.')

      closeTermosResponsabilidadeModal()

      solicitarEntradaVideochamada(
        values.nomeParticipante,
        values.cpf,
        autorizacoes[AtorVideochamada.PROFISSIONAL_EXTERNO]
      )
    },
    [alert, closeTermosResponsabilidadeModal, solicitarEntradaVideochamada]
  )

  const renderForm = ({ values, handleSubmit }: FormRenderProps<SalaEsperaFormModel>) => (
    <form onSubmit={handleSubmit} noValidate>
      <Route path={`${matchUrl}/termosResponsabilidade`}>
        <TermoModal
          onAccept={(autorizacoes) => handleSolicitarEntrada(values, autorizacoes)}
          open
          onClose={() => closeTermosResponsabilidadeModal()}
          termos={termosVideochamadaProfissionalVisitante}
          confirmText='Pedir para ingressar'
        />
      </Route>
      <HideOnScreenSize up='md'>
        <SalaEsperaVideochamadaUsuariosExternosViewMobile
          nomeProfissional={ownerName}
          cboProfissional={cboProfissional}
          audioEnabled={audioEnabled}
          videoEnabled={videoEnabled}
          setAudioEnabled={setAudioEnabled}
          setVideoEnabled={setVideoEnabled}
          statusVideochamada={status}
          statusSolicitacao={statusSolicitacao}
          handleEntrarButtonClick={handleEntrarButtonClick}
        />
      </HideOnScreenSize>
      <HideOnScreenSize down='md'>
        <SalaEsperaVideochamadaUsuariosExternosView
          nomeProfissional={ownerName}
          cboProfissional={cboProfissional}
          audioEnabled={audioEnabled}
          videoEnabled={videoEnabled}
          setAudioEnabled={setAudioEnabled}
          setVideoEnabled={setVideoEnabled}
          statusVideochamada={status}
          statusSolicitacao={statusSolicitacao}
          handleEntrarButtonClick={handleEntrarButtonClick}
        />
      </HideOnScreenSize>
    </form>
  )
  if (
    isSessionLoading ||
    loading ||
    isStatusVideochamadaLoading ||
    status === null ||
    status === StatusVideochamadaEnum.NAO_ENCONTRADA ||
    isAwaitingReconnect
  ) {
    return <LoadingIndicator />
  }
  if (status === StatusVideochamadaEnum.CANCELADA) {
    return <VideochamadaCanceladaView />
  }
  if (status === StatusVideochamadaEnum.AGUARDANDO_INICIO && isOwner) {
    return (
      <CriarVideochamadaView horarioInicioVideochamada={dataVideochamada?.videochamada?.agendamento?.horarioInicial} />
    )
  }
  if (status === StatusVideochamadaEnum.ENCERRADA) {
    return <VideochamadaEncerradaView />
  }
  if (isAnfitriaoForaDaChamadaAtual) {
    if (isEntrarDireto) {
      return <LoadingIndicator />
    }
    return (
      <Fragment>
        <Route path={`${matchUrl}/termosResponsabilidade`}>
          <TermoModal
            open
            termos={termosVideochamadaProfissionalPec}
            readonly={reingressoTermoReadonly}
            onAccept={handleAnfitriaoAceitarTermo}
            onClose={() => closeTermosResponsabilidadeModal()}
            confirmText='Entrar na chamada'
            style={css`
              height: ${reingressoTermoReadonly ? '35.75rem' : '44.25rem'};
            `}
          />
        </Route>
        <SalaEsperaVideochamadaAnfitriaoView
          nomeProfissional={ownerName}
          cboProfissional={cboProfissional}
          audioEnabled={audioEnabled}
          videoEnabled={videoEnabled}
          setAudioEnabled={setAudioEnabled}
          setVideoEnabled={setVideoEnabled}
          handleEntrarButtonClick={handleAnfitriaoEntrarButtonClick}
          onVisualizarTermo={handleVisualizarTermoAnfitriao}
        />
      </Fragment>
    )
  }
  if (selfData) {
    return (
      <VideochamadaToastProvider>
        <VideochamadaView
          selfData={selfData}
          isOwner={isOwner}
          videochamadaUuid={videochamadaUuid}
          audioEnabled={audioEnabled}
          videoEnabled={videoEnabled}
          setAudioEnabled={setAudioEnabled}
          setVideoEnabled={setVideoEnabled}
          hasConnectionFailed={hasConnectionFailed}
        />
      </VideochamadaToastProvider>
    )
  }
  return (
    <Form<SalaEsperaFormModel>
      render={renderForm}
      onSubmit={handleEntrarButtonClick}
      validate={profParticipanteValidator}
      suppressNotificationError
    />
  )
}

function navegarPorEncerramento(
  motivoEncerramento: MotivoEncerramentoVideochamadaEnum | undefined,
  isOwner: boolean,
  history: ReturnType<typeof useHistory>
) {
  switch (motivoEncerramento) {
    case MotivoEncerramentoVideochamadaEnum.ENCERRADA_POR_CRIADOR:
      history.push(`/videochamada/encerrada?owner=${isOwner}`)
      break
    case MotivoEncerramentoVideochamadaEnum.TIMEOUT:
      history.push('/videochamada/timeout')
      break
    case MotivoEncerramentoVideochamadaEnum.CONEXAO_FALHOU:
      showConnectionFailModal(isOwner, () => history.push(`/videochamada/encerrada?owner=${isOwner}`))
      break
    default:
      history.push('/videochamada/encerrada')
      break
  }
}
